'use client'

import CreationTool from 'creation/src/creation-tools/types/creation-tool'
import CreationToolUserRestriction from 'creation/src/creation-tools/types/user-restriction'
import { useAuthModal } from '@/context/auth-modal-context'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import Background from './background'
import { type CreationLoginContinuation } from './store/create-store'
import { CreationStoreProvider, useCreationStore } from './store/provider'
import { trackStepPageView } from './track-step-page-view'

type Props = {
    children: ReactNode
    tool: CreationTool
    basePath: string
    isLoggedIn: boolean
    userRestrictionAccess: CreationToolUserRestriction[]
}

/**
 * `AuthModal` is already mounted once, globally, by `AuthModalContextManager`
 * (see context/manager.tsx) — it is driven by context, not local state, and
 * takes no props. `CreationShell` renders `CreationStoreProvider`, so it
 * cannot call `useCreationStore` on itself; this bridge is a descendant of
 * the provider instead, giving it access to both `useCreationStore` (to flip
 * `isLoggedIn` the moment sign-in succeeds) and `useAuthModal` (to open it).
 * It publishes its `onRequireLogin` implementation onto a ref that
 * `CreationShell` created and handed down to the store at construction time.
 */
const CreationAuthBridge = ({
    requireLoginRef,
}: {
    requireLoginRef: React.MutableRefObject<(onSuccess?: CreationLoginContinuation) => void>
}) => {
    const router = useRouter()
    const { openAuthModal } = useAuthModal()
    const setIsLoggedIn = useCreationStore((state) => state.setIsLoggedIn)
    const resumePendingFiles = useCreationStore((state) => state.resumePendingFiles)

    useEffect(() => {
        requireLoginRef.current = (resumeAfterLogin) => {
            openAuthModal({
                onSuccess: async () => {
                    // The store's isLoggedIn is a mutable field read at call
                    // time by setFiles, distinct from the isLoggedIn prop
                    // below (which is derived server-side and won't change on
                    // a client-side sign-in). Flip it immediately so a user
                    // who signs in from this prompt isn't permanently
                    // blocked.
                    setIsLoggedIn(true)
                    // Refresh the route so the next server render picks up the
                    // real, server-derived isLoggedIn/permissions.
                    router.refresh()
                    if (resumeAfterLogin) {
                        await resumeAfterLogin()
                        return
                    }
                    // Divergence from legacy (spec §13): legacy's setFiles does
                    // NOT re-run after sign-in (manager.tsx:85-89), so the user
                    // has to re-pick the file they already chose. Replay the
                    // stashed selection instead. Runs last because it navigates
                    // to the first step on success.
                    resumePendingFiles()
                },
            })
        }
    })

    return null
}

const CreationShell = ({ children, tool, basePath, isLoggedIn, userRestrictionAccess }: Props) => {
    const router = useRouter()
    const pathname = usePathname()
    const requireLoginRef = useRef<(onSuccess?: CreationLoginContinuation) => void>(() => {})
    const navigate = useCallback((url: string) => router.push(url), [router])
    const onRequireLogin = useCallback(
        (onSuccess?: CreationLoginContinuation) => requireLoginRef.current(onSuccess),
        []
    )

    // Ported from legacy manager.tsx:75-82. `lastPathnameRef` starts at
    // `basePath`, not the pathname on mount, so landing directly on the
    // tool's base (welcome) route fires nothing here — that page view is
    // already covered by the normal hard-navigation page load. Any other
    // entry point, and every subsequent step change, differs from
    // `basePath`/the previous pathname and fires a virtual page view. The
    // ref (not just the `[pathname]` dependency array) is what guarantees a
    // re-render at the same path never re-fires even if this effect later
    // grows other dependencies.
    const lastPathnameRef = useRef<string>(basePath)

    useEffect(() => {
        if (pathname !== lastPathnameRef.current) {
            lastPathnameRef.current = pathname
            trackStepPageView(pathname)
        }
    }, [pathname])

    return (
        <CreationStoreProvider
            tool={tool}
            basePath={basePath}
            isLoggedIn={isLoggedIn}
            userRestrictionAccess={userRestrictionAccess}
            navigate={navigate}
            onRequireLogin={onRequireLogin}
        >
            <CreationAuthBridge requireLoginRef={requireLoginRef} />
            {/*
             * Legacy mounts <Background /> inside the same provider tree
             * (app.tsx:72), which is what lets it read `outputType` for the
             * sticker checkerboard. It has to live inside CreationStoreProvider
             * here for the same reason — it is fixed-position and z-[-10], so
             * its position in the DOM does not affect the layout the
             * measurement harnesses capture.
             */}
            <Background basePath={basePath} />
            {/*
             * Legacy: `body { margin: 0 auto; width: 1040px }` (site/css/global-css.js:11-23)
             * — the creation tools screen is NOT responsive (measurements.md §0.1,
             * product-owner decided 2026-08-08: strict port, no breakpoints, no fluid
             * container). `WelcomeContainer`'s own `w-full` only reproduces that if
             * *something* upstream is fixed at 1040px; nothing was before this, so at
             * any viewport narrower than 1040 the whole screen quietly reflowed
             * instead of staying put and letting the page scroll horizontally.
             *
             * `mx-auto` is legacy's `margin: 0 auto` and is NOT optional: without it
             * the column sits flush left on any viewport wider than 1040px. That is
             * invisible to the measurement harnesses if they only capture at 1040 and
             * 776 — at those widths a 1040px column is at left: 0 either way — which
             * is exactly how it shipped once. Both harnesses now also capture at 1440.
             *
             * Scoped to `CreationShell` (not `<body>`) so it only affects `/create/*`
             * and `/upload` — the rest of the site stays responsive.
             */}
            <div className="mx-auto w-[1040px] min-w-[1040px]">{children}</div>
        </CreationStoreProvider>
    )
}

export default CreationShell
